// ToDo: Review the name of the component
import React, { SyntheticEvent, useCallback, useEffect, useRef } from 'react';

import { Flex } from '../../component-primitives';
import { Box } from '../../component-primitives/box';
import { FormControl } from '../../component-primitives/form-control';
import { FormLabel } from '../form-label';
import { Input } from '../input';
import { useDateValues } from './hooks/use-date-values';
import { DateInputProps } from './types';

const DateInput = ({
  namePrefix = 'date_input',
  shouldHideDayInput = false,
  value = '',
  setValue,
  disabled,
  onBlur,
  dayLabel,
  dayPlaceholder,
  monthLabel,
  monthPlaceholder,
  yearLabel,
  yearPlaceholder,
  'data-test-id': dataTestId,
  variant,
}: DateInputProps) => {
  const {
    dayValue,
    setDayValue,
    monthValue,
    setMonthValue,
    yearValue,
    setYearValue,
  } = useDateValues({ value, shouldHideDayInput, setValue });

  const handleChange = useCallback(
    (event: SyntheticEvent) => {
      if (event.target instanceof HTMLInputElement) {
        const inputValue = event.target.value.replace(/\D/, '');
        switch (event.target.name) {
          case `${namePrefix}_day`:
            return setDayValue(inputValue);
          case `${namePrefix}_month`:
            return setMonthValue(inputValue);
          case `${namePrefix}_year`:
            return setYearValue(inputValue);
        }
      }
    },
    [namePrefix]
  );

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      timeoutRef.current = setTimeout(() => {
        if (
          document.activeElement !== dayRef.current &&
          document.activeElement !== monthRef.current &&
          document.activeElement !== yearRef.current
        ) {
          onBlur?.(event);
        }
      }, 200);
    },
    []
  );

  useEffect(() => {
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
  }, []);

  return (
    <Flex gap={3} data-test-id={dataTestId}>
      {!shouldHideDayInput && (
        <Box width="16">
          <FormControl>
            {dayLabel && <FormLabel>{dayLabel}</FormLabel>}
            <Input
              name={`${namePrefix}_day`}
              ref={dayRef}
              pattern="[0-9]{2}"
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={dayPlaceholder || 'dd'}
              value={dayValue || ''}
              disabled={disabled}
              data-test-id={dataTestId && `${dataTestId}-day`}
              variant={variant}
            />
          </FormControl>
        </Box>
      )}
      <Box width="16">
        <FormControl>
          {monthLabel && <FormLabel>{monthLabel}</FormLabel>}
          <Input
            name={`${namePrefix}_month`}
            ref={monthRef}
            pattern="[0-9]{2}"
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={monthPlaceholder || 'mm'}
            value={monthValue || ''}
            disabled={disabled}
            data-test-id={dataTestId && `${dataTestId}-month`}
            variant={variant}
          />
        </FormControl>
      </Box>
      <Box width="20">
        <FormControl size="large">
          {yearLabel && <FormLabel>{yearLabel}</FormLabel>}
          <Input
            name={`${namePrefix}_year`}
            ref={yearRef}
            pattern="[0-9]{4}"
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={yearPlaceholder || 'yyyy'}
            value={yearValue || ''}
            disabled={disabled}
            data-test-id={dataTestId && `${dataTestId}-year`}
            variant={variant}
          />
        </FormControl>
      </Box>
    </Flex>
  );
};

export { DateInput };
